// Parser for the ESP -> browser stream. Format is defined in
// firmware/main/stream.h: raw data bytes with in-band events after 0xFE.

const ESC = 0xfe;

// CRC-16/CCITT-FALSE (init 0xFFFF, poly 0x1021), as in firmware/main/link_logic.h
export function crc16(bytes, start = 0, end = bytes.length) {
  let crc = 0xffff;
  for (let i = start; i < end; i++) {
    crc ^= bytes[i] << 8;
    for (let b = 0; b < 8; b++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}

// The ESP -> page stream arrives in frames  C3 3C epoch boot seq len payload crc16  (firmware/main/link_logic.h): the
// CP210x USB bridge replays stale packets and drops others after a program opens the port, so every frame has a number
// and a CRC, and the page acknowledges what it has (`onAck(epoch, boot, nextSeq)`); missing frames are sent again.
// `epoch` is chosen by the page for each connection (it is sent to the ESP in CMD_OPEN): frames left over in the bridge
// from an earlier session carry another one and are ignored. `boot` is chosen by the ESP at start-up: a frame with a
// new boot and number 0 means the ESP restarted. push() calls `onPayload(bytes)` with the stream bytes that are new and
// in order; everything outside a valid frame is dropped.
export class LinkReceiver {
  constructor(epoch, { onPayload, onAck }) {
    this.epoch = epoch;
    this.onPayload = onPayload;
    this.onAck = onAck;
    this.boot = null;
    this.expected = 0;
    this.buf = new Uint8Array(0);
    this.skipped = 0; // bytes dropped while looking for a frame
    this.frames = 0; // valid frames of this session, repeated ones included
  }

  // Length of the complete, valid frame that starts at b[i], else 0.
  static frameAt(b, i) {
    if (b[i] !== 0xc3 || b[i + 1] !== 0x3c || i + 6 > b.length) return 0;
    const n = b[i + 5];
    if (n < 1 || n > 240 || i + 8 + n > b.length) return 0;
    return crc16(b, i + 2, i + 6 + n) === ((b[i + 6 + n] << 8) | b[i + 7 + n]) ? 8 + n : 0;
  }

  push(chunk) {
    let b = chunk;
    if (this.buf.length) {
      b = new Uint8Array(this.buf.length + chunk.length);
      b.set(this.buf);
      b.set(chunk, this.buf.length);
    }
    let i = 0;
    let acked = false;
    while (i < b.length) {
      const len = LinkReceiver.frameAt(b, i);
      if (!len) {
        // Not a frame (yet). Junk that looks like a header must not hold up a real frame that follows it: if the
        // candidate is still incomplete, jump to the next complete valid frame inside the buffer, if there is one.
        const n = b[i + 5];
        const incomplete = b[i] === 0xc3 && i + 1 < b.length && b[i + 1] === 0x3c && (i + 6 > b.length || (n >= 1 && n <= 240 && i + 8 + n > b.length));
        if (incomplete) {
          let j = i + 1;
          while (j < b.length && !LinkReceiver.frameAt(b, j)) j++;
          if (j >= b.length) break; // wait for more
          this.skipped += j - i;
          i = j;
          continue;
        }
        if (b[i] === 0xc3 && i + 1 >= b.length) break; // the first magic byte at the very end
        i++;
        this.skipped++;
        continue;
      }
      const [epoch, boot, seq] = [b[i + 2], b[i + 3], b[i + 4]];
      const payload = b.subarray(i + 6, i + len - 2);
      i += len;
      if (epoch !== this.epoch) continue;
      if (boot !== this.boot) {
        if (seq !== 0) continue; // left over in the bridge from an earlier session
        this.boot = boot;
        this.expected = 0;
      }
      this.frames++;
      if (seq === this.expected) {
        this.expected = (this.expected + 1) & 0xff;
        this.onPayload(payload.slice());
      }
      acked = true;
    }
    // one acknowledgement per chunk, not per frame: the ESP reads commands in the middle of a bus transfer and its
    // queue is small (cmd.c)
    if (acked) this.onAck(this.epoch, this.boot, this.expected);
    this.buf = i < b.length ? b.slice(i) : new Uint8Array(0);
  }
}

export class StreamParser {
  // Callbacks: onHello(version), onBoot(reason), onNewJob(why, cut) (why: 1 boot, 2 reset, 3 button; cut: the previous job ended inside a transfer), onPageBreak(), onListen(sa), onUnlisten(), onData(Uint8Array),
  // onEoi(), onReset(), onResponse(Uint8Array) (answer to a device command, see device.js), onPinPress(pin) (button discovery). onData receives runs of data bytes (possibly split
  // across chunks); events are delivered in stream order.
  constructor(handlers) {
    this.h = handlers;
    this.resp = null; // response bytes being collected
    this.state = 'data'; // 'data' | 'esc' | 'listen-sa' | 'hello-ver' | 'boot-reason' | 'resp-len' | 'resp-body' | 'pin-press' | 'newjob-why' | 'newjob-cut'
  }

  push(chunk) {
    let runStart = -1;
    const flush = (end) => {
      if (runStart >= 0 && end > runStart) this.h.onData?.(chunk.subarray(runStart, end));
      runStart = -1;
    };
    for (let i = 0; i < chunk.length; i++) {
      const b = chunk[i];
      switch (this.state) {
        case 'data':
          if (b === ESC) {
            flush(i);
            this.state = 'esc';
          } else if (runStart < 0) {
            runStart = i;
          }
          break;
        case 'esc':
          this.state = 'data';
          if (b === 0x00) this.h.onData?.(Uint8Array.of(ESC));
          else if (b === 0x01) this.state = 'listen-sa';
          else if (b === 0x02) this.h.onUnlisten?.();
          else if (b === 0x03) this.h.onEoi?.();
          else if (b === 0x04) this.h.onReset?.();
          else if (b === 0x05) this.h.onPageBreak?.();
          else if (b === 0x06) this.state = 'hello-ver';
          else if (b === 0x07) this.state = 'boot-reason';
          else if (b === 0x08) this.state = 'resp-len';
          else if (b === 0x09) this.state = 'pin-press';
          else if (b === 0x0a) this.state = 'newjob-why';
          // unknown event: ignore (forward compatibility)
          break;
        case 'listen-sa':
          this.state = 'data';
          this.h.onListen?.(b);
          break;
        case 'boot-reason':
          this.state = 'data';
          this.h.onBoot?.(b);
          break;
        case 'newjob-why':
          this.why = b;
          this.state = 'newjob-cut';
          break;
        case 'newjob-cut':
          this.state = 'data';
          this.h.onNewJob?.(this.why, b !== 0);
          break;
        case 'pin-press':
          this.state = 'data';
          this.h.onPinPress?.(b);
          break;
        case 'resp-len':
          // length-delimited, so the bytes are not escaped
          this.resp = { need: b, bytes: new Uint8Array(b), got: 0 };
          if (b === 0) {
            this.state = 'data';
            this.h.onResponse?.(this.resp.bytes);
          } else {
            this.state = 'resp-body';
          }
          break;
        case 'resp-body':
          this.resp.bytes[this.resp.got++] = b;
          if (this.resp.got === this.resp.need) {
            this.state = 'data';
            this.h.onResponse?.(this.resp.bytes);
          }
          break;
        case 'hello-ver':
          this.state = 'data';
          this.h.onHello?.(b);
          break;
      }
    }
    flush(chunk.length);
  }
}

// Groups parser events into transfers: one transfer per LISTEN..UNLISTEN span on
// a data channel (secondary 0x60|ch). OPEN (0xF0|ch) and CLOSE (0xE0|ch)
// frames carry no print data and are skipped.
export class TransferCollector {
  constructor(onTransfer) {
    this.onTransfer = onTransfer;
    this.cur = null;
  }

  handlers() {
    return {
      onListen: (sa) => {
        this.cur = { sa, channel: sa & 0x0f, kind: sa & 0xf0, chunks: [], size: 0, eoi: false };
      },
      onData: (bytes) => {
        if (!this.cur) return;
        this.cur.chunks.push(bytes.slice());
        this.cur.size += bytes.length;
      },
      onEoi: () => {
        if (this.cur) this.cur.eoi = true;
      },
      onUnlisten: () => this.finish(),
      onReset: () => {
        this.finish();
      },
    };
  }

  // Forget a transfer that is still running (the page switched to another job).
  reset() {
    this.cur = null;
  }

  // Bytes of the transfer that is still running (not yet unlistened), 0 if none.
  pendingSize() {
    return this.cur?.size ?? 0;
  }

  finish() {
    const transfer = this.cur;
    this.cur = null;
    if (!transfer || transfer.size === 0) return;
    const data = new Uint8Array(transfer.size);
    let o = 0;
    for (const c of transfer.chunks) {
      data.set(c, o);
      o += c.length;
    }
    this.onTransfer({ sa: transfer.sa, channel: transfer.channel, eoi: transfer.eoi, data });
  }
}

// Fans parser events out to several handler objects, in order.
export function tee(...hs) {
  const names = ['onHello', 'onBoot', 'onNewJob', 'onPageBreak', 'onResponse', 'onPinPress', 'onListen', 'onUnlisten', 'onData', 'onEoi', 'onReset'];
  return Object.fromEntries(names.map((n) => [n, (...a) => hs.forEach((h) => h[n]?.(...a))]));
}

// VICE's "raw" printer output is only the bytes the C64 sent: no LISTEN, EOI or other events, and a
// 0xFE is plain data. Turn it into a wire stream: one transfer on channel 0 (graphics / uppercase),
// 0xFE escaped.
export function viceRawToWire(raw, sa = 0x60) {
  const out = [0xfe, 0x01, sa];
  for (const b of raw) {
    out.push(b);
    if (b === ESC) out.push(0x00);
  }
  out.push(0xfe, 0x03, 0xfe, 0x02); // the last byte was the end of the data, then UNLISTEN
  return Uint8Array.from(out);
}
